export const FINANCE_OFFICE_SCHEMA_VERSION = 1;

export const FINANCE_DOCUMENT_PRIVACY_SETTINGS_KEY =
  'restack.finance.documentPrivacy.v1';

export const FINANCE_DOCUMENT_CLASSIFICATION =
  'licensed_financial_data';

export const FINANCE_OFFICE_RECEIPT_STATUS = Object.freeze({
  NOT_COMMITTED: 'not_committed',
  COMMITTED: 'committed',
  COMMITTED_AFTER_CANCEL: 'committed_after_cancel',
  INDETERMINATE: 'indeterminate',
});

export const FINANCE_OFFICE_COMMAND = Object.freeze({
  POWERPOINT_INSERT_TEXT: 'powerpoint.insert_text',
  EXCEL_WRITE_TABLE: 'excel.write_table',
  WORD_INSERT_TEXT: 'word.insert_text',
});

export const FINANCE_OFFICE_TOOL = Object.freeze({
  PREPARE: 'prepare_finance_office_operation',
  COMMIT: 'commit_finance_office_operation',
  RECOVER: 'recover_finance_office_operation',
});

const OPERATION_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const STEP_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/;
const DOCUMENT_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SLIDE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:{}-]{0,127}$/;
const WORKSHEET_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:{}-]{0,127}$/;
const EXCEL_CELL_RE = /^([A-Z]{1,3})([1-9]\d{0,6})$/;
const CITATION_LINE_RE =
  /^Source: Daloopa \| [A-Za-z0-9 ._:/()&,-]{1,80} \| [a-z][a-z0-9_:-]{0,63} \| \d{4}-\d{2}-\d{2} \| cx_[A-Za-z0-9_-]{8,128}$/;

export class FinanceOfficeContractError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'FinanceOfficeContractError';
    this.code = code;
  }
}

function contractError(message) {
  return new FinanceOfficeContractError(
    'invalid_finance_office_operation',
    message
  );
}

function strictRecord(value, label) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw contractError(`${label} must be an object`);
  }
  return value;
}

function assertExactKeys(value, expectedKeys, label) {
  const actual = Object.keys(value).sort();
  const expected = [...expectedKeys].sort();
  if (
    actual.length !== expected.length ||
    actual.some((key, index) => key !== expected[index])
  ) {
    throw contractError(`${label} contains unsupported fields`);
  }
}

function boundedString(value, label, maxLength = 2000) {
  if (typeof value !== 'string' || value.length < 1 || value.length > maxLength) {
    throw contractError(`${label} is invalid`);
  }
  return value;
}

function validateCitationLines(value) {
  if (!Array.isArray(value) || value.length < 1 || value.length > 100) {
    throw contractError('citationLines is invalid');
  }
  const unique = new Set();
  return value.map((line) => {
    if (
      typeof line !== 'string' ||
      line.length > 320 ||
      !CITATION_LINE_RE.test(line) ||
      unique.has(line)
    ) {
      throw contractError('citationLines is invalid');
    }
    unique.add(line);
    return line;
  });
}

function validatePowerPointPayload(payload) {
  strictRecord(payload, 'payload');
  assertExactKeys(payload, ['blocks', 'citationLines'], 'payload');
  if (!Array.isArray(payload.blocks) || payload.blocks.length < 1 || payload.blocks.length > 20) {
    throw contractError('PowerPoint blocks are invalid');
  }
  const blocks = payload.blocks.map((block) => {
    strictRecord(block, 'PowerPoint block');
    assertExactKeys(block, ['role', 'text'], 'PowerPoint block');
    if (!['title', 'body', 'metric'].includes(block.role)) {
      throw contractError('PowerPoint block role is invalid');
    }
    return Object.freeze({
      role: block.role,
      text: boundedString(block.text, 'PowerPoint block text'),
    });
  });
  return Object.freeze({
    blocks: Object.freeze(blocks),
    citationLines: Object.freeze(validateCitationLines(payload.citationLines)),
  });
}

function validExcelCell(value) {
  if (typeof value !== 'string') return false;
  const match = EXCEL_CELL_RE.exec(value);
  if (!match) return false;
  const column = [...match[1]].reduce(
    (total, character) => (total * 26) + character.charCodeAt(0) - 64,
    0
  );
  return column <= 16_384 && Number(match[2]) <= 1_048_576;
}

function validateExcelScalar(value) {
  if (value === null || typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (
    typeof value === 'string' &&
    value.length <= 2000 &&
    !/^(?:=|\+|-|@|\{=)/.test(value.trim())
  ) {
    return value;
  }
  throw contractError('Excel cell value is invalid');
}

function validateExcelPayload(payload) {
  strictRecord(payload, 'payload');
  assertExactKeys(payload, ['cells', 'citationLines'], 'payload');
  if (!Array.isArray(payload.cells) || payload.cells.length < 1 || payload.cells.length > 500) {
    throw contractError('Excel cells are invalid');
  }
  const width = payload.cells[0]?.length;
  if (!Number.isSafeInteger(width) || width < 1 || width > 30) {
    throw contractError('Excel cells are invalid');
  }
  if (payload.cells.length * width > 5000) {
    throw contractError('Excel cells are too large');
  }
  const cells = payload.cells.map((row) => {
    if (!Array.isArray(row) || row.length !== width) {
      throw contractError('Excel rows must have equal width');
    }
    return Object.freeze(row.map(validateExcelScalar));
  });
  return Object.freeze({
    cells: Object.freeze(cells),
    citationLines: Object.freeze(validateCitationLines(payload.citationLines)),
  });
}

function validateWordPayload(payload) {
  strictRecord(payload, 'payload');
  assertExactKeys(payload, ['paragraphs', 'citationLines'], 'payload');
  if (
    !Array.isArray(payload.paragraphs) ||
    payload.paragraphs.length < 1 ||
    payload.paragraphs.length > 100
  ) {
    throw contractError('Word paragraphs are invalid');
  }
  return Object.freeze({
    paragraphs: Object.freeze(
      payload.paragraphs.map((text) =>
        boundedString(text, 'Word paragraph text')
      )
    ),
    citationLines: Object.freeze(validateCitationLines(payload.citationLines)),
  });
}

function expectedWordTag(operationId) {
  return `restack_finance_${operationId}`;
}

function validateTarget(command, target, operationId) {
  strictRecord(target, 'target');
  if (command === FINANCE_OFFICE_COMMAND.POWERPOINT_INSERT_TEXT) {
    assertExactKeys(target, ['requireEmpty', 'slideId'], 'target');
    if (
      target.requireEmpty !== true ||
      typeof target.slideId !== 'string' ||
      !SLIDE_ID_RE.test(target.slideId)
    ) {
      throw contractError('PowerPoint target is invalid');
    }
    return Object.freeze({
      slideId: target.slideId,
      requireEmpty: true,
    });
  }
  if (command === FINANCE_OFFICE_COMMAND.EXCEL_WRITE_TABLE) {
    assertExactKeys(target, ['anchorCell', 'requireEmpty', 'worksheetId'], 'target');
    if (
      target.requireEmpty !== true ||
      typeof target.worksheetId !== 'string' ||
      !WORKSHEET_ID_RE.test(target.worksheetId) ||
      !validExcelCell(target.anchorCell)
    ) {
      throw contractError('Excel target is invalid');
    }
    return Object.freeze({
      worksheetId: target.worksheetId,
      anchorCell: target.anchorCell,
      requireEmpty: true,
    });
  }
  if (command === FINANCE_OFFICE_COMMAND.WORD_INSERT_TEXT) {
    assertExactKeys(target, ['contentControlTag', 'requireEmpty'], 'target');
    if (
      target.requireEmpty !== true ||
      target.contentControlTag !== expectedWordTag(operationId)
    ) {
      throw contractError('Word target is invalid');
    }
    return Object.freeze({
      contentControlTag: target.contentControlTag,
      requireEmpty: true,
    });
  }
  throw contractError('Finance Office command is not supported');
}

function commandApp(command) {
  if (command === FINANCE_OFFICE_COMMAND.POWERPOINT_INSERT_TEXT) return 'powerpoint';
  if (command === FINANCE_OFFICE_COMMAND.EXCEL_WRITE_TABLE) return 'excel';
  if (command === FINANCE_OFFICE_COMMAND.WORD_INSERT_TEXT) return 'word';
  throw contractError('Finance Office command is not supported');
}

export function validateFinanceOfficeOperation(value) {
  const request = strictRecord(value, 'operation');
  assertExactKeys(
    request,
    [
      'app',
      'command',
      'documentId',
      'operationBinding',
      'operationId',
      'payload',
      'schemaVersion',
      'stepId',
      'target',
    ],
    'operation'
  );
  if (request.schemaVersion !== FINANCE_OFFICE_SCHEMA_VERSION) {
    throw contractError('Finance Office schemaVersion is not supported');
  }
  if (typeof request.operationId !== 'string' || !OPERATION_ID_RE.test(request.operationId)) {
    throw contractError('operationId is invalid');
  }
  if (
    typeof request.operationBinding !== 'string' ||
    !OPERATION_ID_RE.test(request.operationBinding)
  ) {
    throw contractError('operationBinding is invalid');
  }
  if (typeof request.stepId !== 'string' || !STEP_ID_RE.test(request.stepId)) {
    throw contractError('stepId is invalid');
  }
  if (typeof request.documentId !== 'string' || !DOCUMENT_ID_RE.test(request.documentId)) {
    throw contractError('documentId is invalid');
  }
  const expectedApp = commandApp(request.command);
  if (request.app !== expectedApp) {
    throw contractError('Finance Office app does not match command');
  }
  const target = validateTarget(request.command, request.target, request.operationId);
  const payload = request.command === FINANCE_OFFICE_COMMAND.POWERPOINT_INSERT_TEXT
    ? validatePowerPointPayload(request.payload)
    : request.command === FINANCE_OFFICE_COMMAND.EXCEL_WRITE_TABLE
      ? validateExcelPayload(request.payload)
      : validateWordPayload(request.payload);

  return Object.freeze({
    schemaVersion: FINANCE_OFFICE_SCHEMA_VERSION,
    operationId: request.operationId,
    operationBinding: request.operationBinding.toLowerCase(),
    stepId: request.stepId,
    documentId: request.documentId,
    app: expectedApp,
    command: request.command,
    target,
    payload,
  });
}

export function validateFinanceOfficeToolName(toolName) {
  if (!Object.values(FINANCE_OFFICE_TOOL).includes(toolName)) {
    throw contractError('Finance Office tool is not supported');
  }
  return toolName;
}

export function isTerminalFinanceOfficeReceipt(status) {
  return Object.values(FINANCE_OFFICE_RECEIPT_STATUS).includes(status);
}

export function wordContentControlTag(operationId) {
  if (typeof operationId !== 'string' || !OPERATION_ID_RE.test(operationId)) {
    throw contractError('operationId is invalid');
  }
  return expectedWordTag(operationId);
}

const financeOfficeContracts = {
  FINANCE_DOCUMENT_CLASSIFICATION,
  FINANCE_DOCUMENT_PRIVACY_SETTINGS_KEY,
  FINANCE_OFFICE_COMMAND,
  FINANCE_OFFICE_RECEIPT_STATUS,
  FINANCE_OFFICE_SCHEMA_VERSION,
  FINANCE_OFFICE_TOOL,
  validateFinanceOfficeOperation,
  validateFinanceOfficeToolName,
  wordContentControlTag,
};

export default financeOfficeContracts;
